import { useState, useCallback } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    Button,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    TooltipProvider,
} from '@/components/ui';
import { Copy, Check } from 'lucide-react';
import { Payment } from '../types';
import PackageBadge from './PackageBadge';
import ProviderBadge from './ProviderBadge';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface PaymentCardProps {
    payment: Payment;
    index: number;
}

/**
 * Mobile-friendly card view for a single payment.
 * Displays key information in a compact, touch-friendly layout.
 */
const PaymentCard = ({ payment, index }: PaymentCardProps) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(payment.transactionId);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    }, [payment.transactionId]);

    const formatDate = (dateString: string) => {
        try {
            return format(new Date(dateString), 'd MMM yyyy', { locale: fr });
        } catch {
            return dateString;
        }
    };

    const truncateId = (id: string, maxLength: number = 14) => {
        if (id.length <= maxLength) return id;
        return `${id.slice(0, maxLength)}...`;
    };

    const formatCurrency = (amount: number, currency: string) => {
        if (currency === 'EUR') return `€${amount.toFixed(2)}`;
        if (currency === 'USD') return `$${amount.toFixed(2)}`;
        return `${amount.toFixed(2)} ${currency}`;
    };

    return (
        <TooltipProvider>
            <Card
                className={cn(
                    'animate-in fade-in-0 slide-in-from-bottom-4',
                    'hover:shadow-md transition-shadow duration-200'
                )}
                style={{
                    animationDelay: `${index * 50}ms`,
                    animationFillMode: 'both',
                }}
            >
                <CardHeader className="pb-3 px-4 pt-4">
                    <div className="flex justify-between items-center">
                        <PackageBadge packageName={payment.packageName} />
                        <span className="text-sm font-medium text-muted-foreground bg-muted/50 px-2 py-1 rounded-md">
                            {formatDate(payment.createdAt)}
                        </span>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4 px-4 pb-5">
                    <div className="flex flex-row items-center gap-4">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold shrink-0">Montant</span>
                        <div className="flex items-baseline gap-2">
                            <span className="font-bold text-lg text-foreground">€{payment.priceInEur.toFixed(2)}</span>
                            {payment.currency !== 'EUR' && (
                                <span className="text-xs text-muted-foreground font-medium">
                                    ({formatCurrency(payment.price, payment.currency)})
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-row items-center gap-4">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold shrink-0">Fournisseur</span>
                        <ProviderBadge provider={payment.provider} />
                    </div>

                    <div className="flex flex-row items-center gap-4">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold shrink-0">Transaction</span>
                        <div className="flex items-center gap-1.5">
                            <code className="text-[11px] bg-muted/80 px-1.5 py-0.5 rounded font-mono">
                                {truncateId(payment.transactionId, 12)}
                            </code>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 hover:bg-muted"
                                        onClick={handleCopy}
                                    >
                                        {copied ? (
                                            <Check className="h-3 w-3 text-green-500" />
                                        ) : (
                                            <Copy className="h-3 w-3 text-muted-foreground" />
                                        )}
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {copied ? 'Copié !' : 'Copier'}
                                </TooltipContent>
                            </Tooltip>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </TooltipProvider>
    );
};

export default PaymentCard;
